import { relations, sql } from 'drizzle-orm'
import { index, pgTable, text, timestamp, uuid } from 'drizzle-orm/pg-core'
import { createInsertSchema, createSelectSchema, createUpdateSchema } from 'drizzle-zod'
import z from 'zod/v4'

import { organization, user } from '../auth'
import { SeatEntitlement } from '../entitlement/seat-entitlement'
import { PremiumSourceEnum } from './enums'
import { Membership, MembershipAddOn } from './membership'

export const Premium = pgTable(
  'premium',
  {
    id: uuid().primaryKey().defaultRandom(),
    childId: text().references(() => user.id, { onDelete: 'set null' }),
    // Provenance of this seat. Nullable for now (backfilled from the FK below; a follow-up
    // migration tightens this to NOT NULL once existing rows are backfilled).
    source: PremiumSourceEnum(),
    membershipId: uuid().references(() => Membership.id, { onDelete: 'cascade' }),
    membershipAddOnId: uuid().references(() => MembershipAddOn.id, { onDelete: 'cascade' }),
    // Reporting scope key, not a container: a seat provisioned by an entitlement also carries the
    // membershipId of the Membership minted for it, so membership-scoped reads pick it up.
    entitlementId: uuid().references(() => SeatEntitlement.id, { onDelete: 'set null' }),
    organizationId: text().references(() => organization.id, { onDelete: 'cascade' }),
    startDate: timestamp({ mode: 'date', withTimezone: true }).notNull().defaultNow(),
    endDate: timestamp({ mode: 'date', withTimezone: true }).notNull(),
    createdAt: timestamp({ mode: 'date', withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp({ mode: 'date', withTimezone: true }).$onUpdateFn(() => sql`now()`),
  },
  (t) => [
    // Index for membership lookups
    index('idx_premium_membership').on(t.membershipId),

    // Index for membership add-on lookups
    index('idx_premium_membership_add_on').on(t.membershipAddOnId),

    // Index for entitlement lookups (reporting scope key)
    index('idx_premium_entitlement').on(t.entitlementId),

    // Seat-scoped reads. "Is premium / how many seats" is a union over Premium rows filtered by
    // organization or child plus `endDate > now` — not a container lookup — so those reads have no
    // usable index among the FK ones above. Both are ordered by endDate DESC to match the
    // latest-ending-first ordering the seat reads use.
    index('idx_premium_organization_end_date').on(t.organizationId, t.endDate.desc()),
    index('idx_premium_child_end_date').on(t.childId, t.endDate.desc()),
  ],
)

export const premiumRelations = relations(Premium, ({ one }) => ({
  child: one(user, {
    fields: [Premium.childId],
    references: [user.id],
  }),

  organization: one(organization, {
    fields: [Premium.organizationId],
    references: [organization.id],
  }),

  membership: one(Membership, {
    fields: [Premium.membershipId],
    references: [Membership.id],
  }),

  membershipAddOn: one(MembershipAddOn, {
    fields: [Premium.membershipAddOnId],
    references: [MembershipAddOn.id],
  }),

  entitlement: one(SeatEntitlement, {
    fields: [Premium.entitlementId],
    references: [SeatEntitlement.id],
  }),
}))

export const InsertPremiumSchema = createInsertSchema(Premium, {
  childId: z.string().optional(),
  source: z.enum(PremiumSourceEnum.enumValues).optional(),
  membershipId: z.uuid().optional(),
  membershipAddOnId: z.uuid().optional(),
  entitlementId: z.uuid().optional(),
  organizationId: z.string(),
  startDate: z.date(),
  endDate: z.date(),
})
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  })
  .superRefine((data, ctx) => {
    // A seat sits in exactly one container: a membership OR an add-on, never both.
    if (data.membershipId && data.membershipAddOnId) {
      ctx.addIssue({
        code: 'custom',
        message: 'Only one of membershipId or membershipAddOnId may be provided',
        path: ['source'],
      })
    }

    // `entitlementId` is the reporting scope key, not a container: an entitlement-provisioned seat
    // carries both its membershipId (so membership-scoped reads see it) and its entitlementId.
    if (!data.membershipId && !data.membershipAddOnId && !data.entitlementId) {
      ctx.addIssue({
        code: 'custom',
        message: 'One of membershipId, membershipAddOnId, or entitlementId must be provided',
        path: ['source'],
      })
    }

    // If `source` is provided it must match which FK is set.
    if (data.source === 'membership' && !data.membershipId) {
      ctx.addIssue({ code: 'custom', message: "source 'membership' requires membershipId", path: ['membershipId'] })
    }
    if (data.source === 'add_on' && !data.membershipAddOnId) {
      ctx.addIssue({
        code: 'custom',
        message: "source 'add_on' requires membershipAddOnId",
        path: ['membershipAddOnId'],
      })
    }
    if (data.source === 'entitlement' && !data.entitlementId) {
      ctx.addIssue({ code: 'custom', message: "source 'entitlement' requires entitlementId", path: ['entitlementId'] })
    }

    // Validate that endDate is after startDate
    if (data.endDate <= data.startDate) {
      ctx.addIssue({
        code: 'custom',
        message: 'End date must be after start date',
        path: ['endDate'],
      })
    }
  })

export const UpdatePremiumSchema = createUpdateSchema(Premium, {
  childId: z.string().optional(),
  endDate: z.date().optional(),
})
  .omit({
    id: true,
    source: true,
    membershipId: true,
    membershipAddOnId: true,
    entitlementId: true,
    organizationId: true,
    startDate: true,
    createdAt: true,
    updatedAt: true,
  })
  .partial()

export const SelectPremiumSchema = createSelectSchema(Premium, {
  id: z.uuid(),
  childId: z.string().nullable(),
  source: z.enum(PremiumSourceEnum.enumValues).nullable(),
  membershipId: z.uuid().nullable(),
  membershipAddOnId: z.uuid().nullable(),
  entitlementId: z.uuid().nullable(),
  organizationId: z.string().nullable(),
  startDate: z.date(),
  endDate: z.date(),
  createdAt: z.date(),
  updatedAt: z.date().nullable(),
})

export type InsertPremium = typeof Premium.$inferInsert
export type UpdatePremium = z.infer<typeof UpdatePremiumSchema>
export type SelectPremium = typeof Premium.$inferSelect
